import React, { useEffect, useRef, useState } from 'react';
import { GoogleIcon } from './SocialIcons';

/**
 * "Continue with Google" button backed by Google Identity Services (GIS).
 *
 * The backend verifies a Google *ID token* server-side (GoogleIdTokenVerifier),
 * so this is the ID-token flow — NOT the Spring redirect flow. GIS only hands
 * out an ID token through its own rendered button (or One Tap), so we render
 * Google's official button and forward the returned credential (the ID token)
 * to `onCredential`.
 *
 * The web client id comes from REACT_APP_GOOGLE_CLIENT_ID and must match the
 * backend's `app.google.client-id` (that's the audience the verifier checks).
 * Until it's set, we fall back to a styled placeholder button so the form still
 * renders (and tests, which never load the external script, don't break).
 *
 * Styling: GIS renders its button inside a cross-origin iframe we can't
 * restyle, but the product wants the same look as the Meta button. So we paint
 * our own custom-styled button and overlay Google's real (but transparent)
 * button on top of it — the user sees ours, the click lands on Google's.
 */
const GIS_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Load the GIS script once and share the promise across button instances.
let gisPromise = null;
function loadGis() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisPromise) return gisPromise;

  gisPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    const target = existing || document.createElement('script');
    target.addEventListener('load', () => resolve(), { once: true });
    target.addEventListener('error', () => reject(new Error('gis-load')), { once: true });
    if (!existing) {
      target.src = GIS_SRC;
      target.async = true;
      target.defer = true;
      document.head.appendChild(target);
    }
  });
  return gisPromise;
}

function GoogleSignInButton({ onCredential, onError, disabled = false }) {
  const containerRef = useRef(null);
  // Keep the latest callbacks in refs so GIS initialises only once.
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  const [unavailable, setUnavailable] = useState(!CLIENT_ID);

  useEffect(() => {
    if (!CLIENT_ID) return undefined;
    let cancelled = false;

    loadGis()
      .then(() => {
        if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (response) => {
            if (response?.credential) onCredentialRef.current?.(response.credential);
            else onErrorRef.current?.('No hemos recibido el token de Google.');
          },
        });
        const width = Math.min(
          Math.max(containerRef.current.clientWidth || 320, 200),
          400
        );
        window.google.accounts.id.renderButton(containerRef.current, {
          // Rendered invisibly on top of our own button (see file header), so
          // only size matters here — the visuals come from `.Login-social`.
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          locale: 'es',
          width,
        });
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // No client id (or GIS failed to load): keep the original styled button so
  // the layout is preserved and the user gets a clear message.
  if (unavailable) {
    return (
      <button
        type="button"
        className="Login-social"
        onClick={() =>
          onErrorRef.current?.(
            'El acceso con Google no está configurado todavía. Vuelve a intentarlo en breve.'
          )
        }
      >
        <GoogleIcon className="Login-socialIcon" />
        Continuar con Google
      </button>
    );
  }

  // Our custom button (matches the Meta one) with Google's real button laid
  // transparently over it to actually capture the click.
  return (
    <div
      className={`Login-googleWrap${disabled ? ' Login-googleWrap--disabled' : ''}`}
    >
      <span className="Login-social" aria-hidden="true">
        <GoogleIcon className="Login-socialIcon" />
        Continuar con Google
      </span>
      <div className="Login-gisOverlay" ref={containerRef} />
    </div>
  );
}

export default GoogleSignInButton;
